import React, { useState, useRef, useEffect } from 'react';
import Button from './button';
import { cropImage } from '../utils/imageUtils';

interface ImageCropperProps {
  imageDataUrl: string;
  onCrop: (croppedImageDataUrl: string) => void;
  onCancel: () => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({ imageDataUrl, onCrop, onCancel }) => {
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1); // Minimum scale is the initial zoom level
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: 0, height: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialPinchScale, setInitialPinchScale] = useState(1);
  const [initialPinchPosition, setInitialPinchPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      
      // Calculate display dimensions to ensure image fills entire container (no black margins)
      // For a square crop area, we need to size based on the SMALLER dimension
      if (containerRef.current) {
        const containerSize = containerRef.current.clientWidth;
        const aspectRatio = img.width / img.height;
        
        let displayWidth, displayHeight;
        if (aspectRatio > 1) {
          // Landscape: height is smaller, so fill based on height to ensure no black bars
          displayHeight = containerSize;
          displayWidth = containerSize * aspectRatio;
        } else {
          // Portrait or square: width is smaller or equal, so fill based on width
          displayWidth = containerSize;
          displayHeight = containerSize / aspectRatio;
        }
        
        setDisplayDimensions({ width: displayWidth, height: displayHeight });
        
        // Set initial scale to 1 (image already sized to fill container)
        const initialScale = 1;
        setScale(initialScale);
        setMinScale(initialScale); // Store as minimum zoom level
      }
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const constrainPosition = (newX: number, newY: number) => {
    if (!containerRef.current) return { x: newX, y: newY };
    
    const containerSize = containerRef.current.clientWidth;
    const scaledWidth = displayDimensions.width * scale;
    const scaledHeight = displayDimensions.height * scale;
    
    // Calculate the maximum allowed offset from center
    // The image edge should not go past the container edge
    const maxOffsetX = (scaledWidth - containerSize) / 2;
    const maxOffsetY = (scaledHeight - containerSize) / 2;
    
    // Clamp position to prevent image edges from going inside the crop area
    const constrainedX = Math.max(-maxOffsetX, Math.min(newX, maxOffsetX));
    const constrainedY = Math.max(-maxOffsetY, Math.min(newY, maxOffsetY));
    
    return { x: constrainedX, y: constrainedY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const newPosition = constrainPosition(
      e.clientX - dragStart.x,
      e.clientY - dragStart.y
    );
    setPosition(newPosition);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Two-finger pinch gesture
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      
      // Store the current state - we'll keep the center of the visible image stationary
      setInitialPinchDistance(distance);
      setInitialPinchScale(scale);
      setInitialPinchPosition({ x: position.x, y: position.y });
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      // Single-finger drag
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance !== null && initialPinchDistance > 0) {
      // Two-finger pinch gesture
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      if (distance > 0) {
        const scaleChange = distance / initialPinchDistance;
        const newScale = Math.max(minScale, Math.min(initialPinchScale * scaleChange, 10));
        
        // Keep the center of the visible image area stationary during zoom
        // The center point in image coordinates should remain at the same position
        // As we change scale, adjust position proportionally to keep center stationary
        const scaleDiff = newScale / initialPinchScale;
        const newX = initialPinchPosition.x * scaleDiff;
        const newY = initialPinchPosition.y * scaleDiff;
        
        setScale(newScale);
        const constrainedPos = constrainPosition(newX, newY);
        setPosition(constrainedPos);
      }
    } else if (e.touches.length === 1 && isDragging) {
      // Single-finger drag
      const touch = e.touches[0];
      const newPosition = constrainPosition(
        touch.clientX - dragStart.x,
        touch.clientY - dragStart.y
      );
      setPosition(newPosition);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setInitialPinchDistance(null);
  };

  const handleZoomIn = () => {
    setScale(prev => {
      const newScale = Math.min(prev * 1.2, 10);
      // After zoom, constrain position to new boundaries
      setTimeout(() => {
        setPosition(currentPos => constrainPosition(currentPos.x, currentPos.y));
      }, 0);
      return newScale;
    });
  };

  const handleZoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev / 1.2, minScale);
      // After zoom, constrain position to new boundaries
      setTimeout(() => {
        setPosition(currentPos => constrainPosition(currentPos.x, currentPos.y));
      }, 0);
      return newScale;
    });
  };

  const handleCrop = async () => {
    if (!containerRef.current) return;

    const containerSize = containerRef.current.clientWidth;
    
    // Calculate the scaled display dimensions
    const scaledDisplayWidth = displayDimensions.width * scale;
    const scaledDisplayHeight = displayDimensions.height * scale;
    
    // The container center is at (containerSize/2, containerSize/2)
    // The image center after transformation is at (containerSize/2 + position.x, containerSize/2 + position.y)
    // The top-left corner of the displayed image in container coordinates is:
    const imageDisplayLeft = containerSize / 2 + position.x - scaledDisplayWidth / 2;
    const imageDisplayTop = containerSize / 2 + position.y - scaledDisplayHeight / 2;
    
    // The crop frame covers the entire container (0, 0, containerSize, containerSize)
    // We need to find what part of the image is visible in this frame
    // Crop area in display coordinates (relative to the displayed image top-left)
    const cropDisplayX = -imageDisplayLeft;
    const cropDisplayY = -imageDisplayTop;
    
    // Convert to original image coordinates
    // The ratio between display dimensions and original dimensions
    const scaleToOriginal = imageDimensions.width / displayDimensions.width;
    
    let cropX = (cropDisplayX / scale) * scaleToOriginal;
    let cropY = (cropDisplayY / scale) * scaleToOriginal;
    let cropSize = (containerSize / scale) * scaleToOriginal;
    
    // Clamp crop area to image boundaries
    cropX = Math.max(0, Math.min(cropX, imageDimensions.width - cropSize));
    cropY = Math.max(0, Math.min(cropY, imageDimensions.height - cropSize));
    cropSize = Math.min(cropSize, imageDimensions.width, imageDimensions.height);
    
    try {
      const croppedImage = await cropImage(
        imageDataUrl,
        {
          x: cropX,
          y: cropY,
          width: cropSize,
          height: cropSize
        },
      );
      onCrop(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/80 flex justify-center items-center z-[1000]">
      <div className="bg-[var(--bg-primary)] rounded-2xl p-6 max-w-[500px] w-[90%] max-h-[90vh] overflow-y-auto">
        <h2 className="text-[var(--text-primary)] text-2xl font-bold m-0 mb-2 text-center">Crop Your Photo</h2>
        <p className="text-[var(--text-secondary)] text-sm m-0 mb-4 text-center">Drag to position, pinch or use buttons to zoom</p>
        
        <div 
          ref={containerRef}
          className="relative w-full aspect-square mb-4 rounded-lg overflow-hidden bg-[var(--bg-secondary)] touch-none select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            ref={imageRef}
            src={imageDataUrl}
            alt="Crop preview"
            className="absolute max-w-none"
            style={{
              top: '50%',
              left: '50%',
              width: `${displayDimensions.width}px`,
              height: `${displayDimensions.height}px`,
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transformOrigin: 'center',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none'
            } as React.CSSProperties}
            draggable={false}
          />
          <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
            <div className="absolute w-[30px] h-[30px] border-3 border-white top-0 left-0 border-r-0 border-b-0"></div>
            <div className="absolute w-[30px] h-[30px] border-3 border-white top-0 right-0 border-l-0 border-b-0"></div>
            <div className="absolute w-[30px] h-[30px] border-3 border-white bottom-0 left-0 border-r-0 border-t-0"></div>
            <div className="absolute w-[30px] h-[30px] border-3 border-white bottom-0 right-0 border-l-0 border-t-0"></div>
          </div>
        </div>
        
        <div className="flex gap-2 items-center justify-center mb-4">
          <button 
            className="w-10 h-10 rounded-full bg-indigo-600 text-white border-none cursor-pointer flex items-center justify-center text-xl font-bold transition-all hover:bg-indigo-700 active:scale-95"
            onClick={handleZoomOut}
            disabled={scale <= minScale}
          >
            −
          </button>
          <span className="text-[var(--text-secondary)] text-sm mx-2">Zoom</span>
          <button 
            className="w-10 h-10 rounded-full bg-indigo-600 text-white border-none cursor-pointer flex items-center justify-center text-xl font-bold transition-all hover:bg-indigo-700 active:scale-95"
            onClick={handleZoomIn}
            disabled={scale >= 10}
          >
            +
          </button>
        </div>
        
        <div className="flex gap-3 justify-center">
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleCrop}>
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
